/**
 * In-memory TTL cache for the /corebase/mix/general/* reference-data endpoint.
 *
 * mixLoad() hits an aggregate endpoint that returns classes, profils, fonctions,
 * all enfants, all employees and several scolarite/cantine collections. ~21 pages
 * call it on mount, so without a cache every navigation re-downloads and re-parses
 * the whole payload.
 *
 * Design:
 *  - one entry per mix pathvar (the "tag=lastUpdate,..." query string),
 *  - concurrent callers during a fetch share the same in-flight promise,
 *  - once resolved, the payload is snapshotted as JSON so every later caller gets
 *    its own independent deep copy (callers' convertToFrontEnd functions may
 *    mutate the objects they receive — a shared object would leak that mutation),
 *  - entries are reused for {@link MIX_TTL_MS} (60s),
 *  - invalidateMix() (wired into the axios response interceptor) drops entries on
 *    any successful business write so edits show up immediately.
 *
 * Plain module-level state — no react/redux/persistence. Safe to import from both
 * loader_cache_helpers and axios_helpers (no import cycle).
 */
export const MIX_TTL_MS = 60_000;

interface MixCacheEntry {
    /** The shared (possibly still in-flight) fetch promise. */
    promise: Promise<any>;
    /** JSON snapshot of the resolved payload, set once the fetch completes. */
    payloadJson?: string;
    /** Creation time of the entry (used for the TTL check). */
    at: number;
}

const mixCache = new Map<string, MixCacheEntry>();

/** Returns a fresh (non-expired) cache entry, or undefined (and evicts stale). */
const getFreshEntry = (pathvar: string): MixCacheEntry | undefined => {
    const entry = mixCache.get(pathvar);
    if (entry == null) {
        return undefined;
    }
    if (Date.now() - entry.at >= MIX_TTL_MS) {
        mixCache.delete(pathvar);
        return undefined;
    }
    return entry;
};

/**
 * Returns an independent deep copy of a previously-resolved mix payload, or
 * undefined when nothing fresh is cached for this pathvar.
 */
export const getMixCachedPayload = (pathvar: string): any | undefined => {
    const entry = getFreshEntry(pathvar);
    if (entry == null || entry.payloadJson == null) {
        return undefined;
    }
    return JSON.parse(entry.payloadJson);
};

/**
 * Returns the shared (possibly still in-flight) fetch promise for a fresh entry,
 * or undefined when the entry is absent/expired. Used to dedupe concurrent
 * callers while a fetch is still running.
 */
export const getMixCachedPromise = (pathvar: string): Promise<any> | undefined => {
    const entry = getFreshEntry(pathvar);
    return entry == null ? undefined : entry.promise;
};

/** Registers a fetch promise for the pathvar (used for in-flight dedup). */
export const setMixCachedPromise = (pathvar: string, promise: Promise<any>): void => {
    mixCache.set(pathvar, {promise, at: Date.now()});
};

/** Snapshots the resolved payload of an existing entry as JSON. */
export const snapshotMixCachedPayload = (pathvar: string, data: any): void => {
    const entry = mixCache.get(pathvar);
    if (entry == null) {
        return;
    }
    entry.payloadJson = JSON.stringify(data);
};

/** Drops a single cache entry (used to avoid caching request failures). */
export const deleteMixCachedPromise = (pathvar: string): void => {
    mixCache.delete(pathvar);
};

/**
 * Drop cached mix payloads.
 * - no argument: clears the whole cache,
 * - a tag (or array of tags): drops only entries that cover those collections.
 */
export const invalidateMix = (tags?: string | string[]): void => {
    if (tags == null) {
        mixCache.clear();
        return;
    }
    const wanted = new Set(Array.isArray(tags) ? tags : [tags]);
    if (wanted.size === 0) {
        return;
    }
    for (const key of Array.from(mixCache.keys())) {
        const keyTags = key.split(",").map((part) => part.split("=")[0]);
        if (keyTags.some((tag) => wanted.has(tag))) {
            mixCache.delete(key);
        }
    }
};
